import { useEffect, useState } from "react";
import { fetchEventsList } from "../../lib/api";
import { toUIEvent, type UIEvent } from "../../lib/events-adapter";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";

/**
 * useArchivedEvents — the Archive page's own read (JAI-2388 / JAI-2669, 2026-10-13).
 * The default events list (useEvents) leaves archived events out by design; only
 * `view=archive` returns them (ended + archived, most recently ended first). This store
 * reads that view, follows hasMore like useEvents (6-page valve), and is shared by the
 * Archive page and the Archive tab count (its `total`).
 *
 * Data: bound to listEvents (GET /v1/admin/events?view=archive). Refetched after every
 * Restore, Archive, Undo and bulk Archive (eventActions.ts), alongside refetchEvents().
 * Tenant switch: purged on switching, re-read on the confirmed change (same rule as useEvents).
 */
export interface ArchivedEventsState {
  events: UIEvent[];
  total: number;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

let _events: UIEvent[] | null = null;
let _total: number | null = null;
let _loading = true;
let _error: string | null = null;
let _inFlight: Promise<void> | null = null;
let _lastFetchedAt = 0;
let _switching = false;
let _epoch = 0;
const FRESH_MS = 30000;
const _subs = new Set<() => void>();
const _emit = () => _subs.forEach((f) => { try { f(); } catch { /* noop */ } });

onTenantSwitching(() => { _switching = true; _epoch++; _events = null; _total = null; _loading = true; _error = null; _inFlight = Promise.resolve(); _lastFetchedAt = 0; _emit(); });
onTenantChange(() => { _switching = false; _inFlight = null; _lastFetchedAt = 0; if (_subs.size > 0 && can("events.read")) void _revalidate(); });

async function _fetch(): Promise<void> {
  const epoch = _epoch;
  try {
    const acc: UIEvent[] = [];
    let page = 1;
    let total: number | null = null;
    for (;;) {
      const res = await fetchEventsList({ view: "archive", page, limit: 500 });
      const batch = ((res.data ?? res.events ?? []) as Parameters<typeof toUIEvent>[0][]).map(toUIEvent);
      acc.push(...batch);
      total = (res as { total?: number }).total ?? null;
      const hasMore = !!(res as { hasMore?: boolean }).hasMore;
      if (!hasMore || batch.length === 0 || page >= 6) break;
      page++;
    }
    if (epoch !== _epoch) return;
    _events = acc;
    _total = total ?? acc.length;
    _error = null;
  } catch (e: unknown) {
    if (epoch !== _epoch) return;
    _error = e instanceof Error ? e.message : "Failed to load archived events.";
    // Keep last-good rows on a transient failure; only a never-loaded store shows the error state.
  } finally {
    if (epoch === _epoch) {
      _lastFetchedAt = Date.now();
      _loading = false;
      _inFlight = null;
      _emit();
    }
  }
}

function _revalidate(): Promise<void> {
  if (_inFlight) return _inFlight;
  _inFlight = _fetch();
  return _inFlight;
}

/** Fresh read chained after any in-flight one, so it reflects a just-committed restore/archive. */
export function refetchArchivedEvents(): Promise<void> {
  const run = (): Promise<void> => { if (_switching) return Promise.resolve(); _inFlight = _fetch(); return _inFlight; };
  return _inFlight ? _inFlight.then(run, run) : run();
}

export function useArchivedEvents(enabled = true): ArchivedEventsState {
  const [, force] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const cb = () => force((n) => n + 1);
    _subs.add(cb);
    if (_events === null || _lastFetchedAt === 0 || Date.now() - _lastFetchedAt >= FRESH_MS) void _revalidate();
    return () => { _subs.delete(cb); };
  }, [enabled]);
  return {
    events: _events ?? [],
    total: _total ?? (_events ?? []).length,
    loading: _events === null && _error === null ? _loading : false,
    error: _events === null ? _error : null,
    reload: () => { _error = null; _loading = true; _emit(); return refetchArchivedEvents(); },
  };
}
